import React, { useEffect, useState } from "react";
import {
  getCategories,
  createCategory,
  updateCategory,
  deleteCategory,
} from "../api";
import { logout } from "../auth"; // ✅ REQUIRED

export default function Categories() {
  const [categories, setCategories] = useState([]);
  const [name, setName] = useState("");
  const [editingId, setEditingId] = useState(null);

  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    try {
      const data = await getCategories();
      setCategories(data || []);
    } catch (err) {
      if (err?.response?.status === 401) {
        logout(); // ✅ works now
      }
    }
  };

  const handleSave = async () => {
    if (!name.trim()) return;

    try {
      if (editingId) {
        await updateCategory(editingId, { name });
      } else {
        await createCategory({ name });
      }

      setName("");
      setEditingId(null);
      loadCategories();
    } catch (err) {
      if (err?.response?.status === 401) {
        logout();
      }
    }
  };

  const handleEdit = (cat) => {
    setName(cat.name);
    setEditingId(cat.id);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this category?")) return;

    try {
      await deleteCategory(id);
      loadCategories();
    } catch (err) {
      if (err?.response?.status === 401) {
        logout();
      }
    }
  };

  return (
    <div className="so-categories p-4 md:p-8 h-full overflow-auto">

      {/* Scoped SwiftOrder theme — same tokens as the rest of the POS. */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&family=Inter:wght@400;500;600;700;800&family=JetBrains+Mono:wght@400;500;600;700&display=swap');

        .so-categories {
          --ink: #1c1a17;
          --paper: #fbf9f4;
          --paper-dim: #f2ede1;
          --chili: #e4572e;
          --chili-dark: #c43f1a;
          --stone: #6b6459;
          --stone-light: #a49c8d;
          --line: #ddd4c2;
          --font-display: 'Bebas Neue', 'Arial Narrow', sans-serif;
          --font-body: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
          --font-mono: 'JetBrains Mono', 'Courier New', monospace;

          background: var(--paper-dim);
          background-image: radial-gradient(circle at 1px 1px, var(--line) 1px, transparent 1.6px);
          background-size: 24px 24px;
          font-family: var(--font-body);
          color: var(--ink);
        }

        .so-categories * {
          box-sizing: border-box;
        }

        .so-cat-title {
          font-family: var(--font-display);
          font-size: 2rem;
          letter-spacing: 0.01em;
          color: var(--ink);
        }

        .so-cat-input {
          border: 1px solid var(--line);
          border-radius: 8px;
          padding: 10px 14px;
          background: #fffdf8;
          color: var(--ink);
          outline: none;
          font-size: 0.95rem;
          transition: border-color 0.15s ease, box-shadow 0.15s ease;
        }

        .so-cat-input:focus {
          border-color: var(--chili);
          box-shadow: 0 0 0 3px rgba(228, 87, 46, 0.15);
        }

        .so-cat-save-btn {
          background: var(--chili);
          color: var(--paper);
          border-radius: 8px;
          box-shadow: 0 10px 22px -10px rgba(228, 87, 46, 0.55);
          transition: background 0.15s ease;
        }

        .so-cat-save-btn:hover {
          background: var(--chili-dark);
        }

        .so-cat-cancel-btn {
          border: 1px solid var(--line);
          color: var(--ink);
          background: #fffdf8;
          border-radius: 8px;
          transition: background 0.15s ease;
        }

        .so-cat-cancel-btn:hover {
          background: var(--paper-dim);
        }

        .so-cat-table-wrap {
          background: #fffdf8;
          border: 1px solid var(--line);
          border-radius: 12px;
          box-shadow: 0 10px 26px -22px rgba(28, 26, 23, 0.3);
        }

        .so-cat-thead {
          background: var(--paper-dim);
          font-family: var(--font-mono);
          font-size: 0.72rem;
          letter-spacing: 0.06em;
          text-transform: uppercase;
          color: var(--stone);
        }

        .so-cat-row {
          border-top: 1px solid var(--line);
          transition: background 0.1s ease;
        }

        .so-cat-row:hover {
          background: var(--paper-dim);
        }

        .so-cat-edit-btn {
          background: var(--paper-dim);
          color: var(--ink);
          border-radius: 6px;
          transition: background 0.15s ease;
        }

        .so-cat-edit-btn:hover {
          background: var(--line);
        }

        .so-cat-delete-btn {
          background: #fdece6;
          color: var(--chili-dark);
          border-radius: 6px;
          transition: background 0.15s ease;
        }

        .so-cat-delete-btn:hover {
          background: #f09595;
        }

        .so-cat-empty {
          color: var(--stone-light);
        }
      `}</style>

      <h2 className="so-cat-title mb-6">Categories</h2>

      <div className="flex flex-col md:flex-row gap-2 mb-6">
        <input
          className="so-cat-input flex-1"
          placeholder="Category name"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />

        <button
          onClick={handleSave}
          className="so-cat-save-btn px-4 py-2"
        >
          {editingId ? "Update" : "Add"}
        </button>

        {editingId && (
          <button
            onClick={() => {
              setName("");
              setEditingId(null);
            }}
            className="so-cat-cancel-btn px-4 py-2"
          >
            Cancel
          </button>
        )}
      </div>

      <div className="so-cat-table-wrap overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="so-cat-thead">
            <tr>
              <th className="px-4 py-3 text-left">#</th>
              <th className="px-4 py-3 text-left">Name</th>
              <th className="px-4 py-3 text-center">Actions</th>
            </tr>
          </thead>

          <tbody>
            {categories.map((c, i) => (
              <tr key={c.id} className="so-cat-row">
                <td className="px-4 py-3">{i + 1}</td>
                <td className="px-4 py-3 font-medium">{c.name}</td>
                <td className="px-4 py-3 text-center">
                  <div className="flex justify-center gap-2">
                    <button
                      onClick={() => handleEdit(c)}
                      className="so-cat-edit-btn px-3 py-1"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(c.id)}
                      className="so-cat-delete-btn px-3 py-1"
                    >
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}

            {categories.length === 0 && (
              <tr>
                <td colSpan="3" className="so-cat-empty text-center py-8">
                  No categories found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}








// import React, { useEffect, useState } from "react";
// import {
//   getCategories,
//   createCategory,
//   updateCategory,
//   deleteCategory,
// } from "../api";
// import { logout } from "../auth"; // ✅ REQUIRED

// export default function Categories() {
//   const [categories, setCategories] = useState([]);
//   const [name, setName] = useState("");
//   const [editingId, setEditingId] = useState(null);

//   useEffect(() => {
//     loadCategories();
//   }, []);

//   const loadCategories = async () => {
//     try {
//       const data = await getCategories();
//       setCategories(data || []);
//     } catch (err) {
//       if (err?.response?.status === 401) {
//         logout(); // ✅ works now
//       }
//     }
//   };

//   const handleSave = async () => {
//     if (!name.trim()) return;

//     try {
//       if (editingId) {
//         await updateCategory(editingId, { name });
//       } else {
//         await createCategory({ name });
//       }

//       setName("");
//       setEditingId(null);
//       loadCategories();
//     } catch (err) {
//       if (err?.response?.status === 401) {
//         logout();
//       }
//     }
//   };

//   const handleEdit = (cat) => {
//     setName(cat.name);
//     setEditingId(cat.id);
//   };

//   const handleDelete = async (id) => {
//     if (!window.confirm("Delete this category?")) return;

//     try {
//       await deleteCategory(id);
//       loadCategories();
//     } catch (err) {
//       if (err?.response?.status === 401) {
//         logout();
//       }
//     }
//   };

//   return (
//     <div className="p-4 md:p-8 h-full overflow-auto">
//       <h2 className="text-2xl md:text-3xl font-bold mb-6">Categories</h2>

//       <div className="flex flex-col md:flex-row gap-2 mb-6">
//         <input
//           className="flex-1 border rounded px-3 py-2"
//           placeholder="Category name"
//           value={name}
//           onChange={(e) => setName(e.target.value)}
//         />

//         <button
//           onClick={handleSave}
//           className="bg-blue-600 text-white px-4 py-2 rounded"
//         >
//           {editingId ? "Update" : "Add"}
//         </button>

//         {editingId && (
//           <button
//             onClick={() => {
//               setName("");
//               setEditingId(null);
//             }}
//             className="border px-4 py-2 rounded"
//           >
//             Cancel
//           </button>
//         )}
//       </div>

//       <div className="bg-white rounded shadow overflow-x-auto">
//         <table className="min-w-full text-sm">
//           <thead className="bg-slate-100">
//             <tr>
//               <th className="px-4 py-2 text-left">#</th>
//               <th className="px-4 py-2 text-left">Name</th>
//               <th className="px-4 py-2 text-center">Actions</th>
//             </tr>
//           </thead>

//           <tbody>
//             {categories.map((c, i) => (
//               <tr key={c.id} className="border-t">
//                 <td className="px-4 py-2">{i + 1}</td>
//                 <td className="px-4 py-2">{c.name}</td>
//                 <td className="px-4 py-2 text-center">
//                   <div className="flex justify-center gap-2">
//                     <button
//                       onClick={() => handleEdit(c)}
//                       className="px-2 py-1 bg-slate-100 rounded"
//                     >
//                       Edit
//                     </button>
//                     <button
//                       onClick={() => handleDelete(c.id)}
//                       className="px-2 py-1 bg-red-100 text-red-700 rounded"
//                     >
//                       Delete
//                     </button>
//                   </div>
//                 </td>
//               </tr>
//             ))}

//             {categories.length === 0 && (
//               <tr>
//                 <td colSpan="3" className="text-center py-6 text-slate-400">
//                   No categories found
//                 </td>
//               </tr>
//             )}
//           </tbody>
//         </table>
//       </div>
//     </div>
//   );
// }
